import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Box, Stack, styled} from '@mui/material';
import {
  Button,
  designSystemToken,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import {ScriptStyle, type GeneratedScript} from '@contentlab/api/avatars/types';
import contentLabStore, {
  AiAvatarEditorTab,
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import translate from '@/utils/translate';
import {WORD_COUNT_TOLERANCE} from '../../../constants';
import {SuggestedScriptHeader, ScriptEditor, WordCount} from './scripts-tab';

const WORDS_PER_SECOND = 2.5;

function getMaxWordsFromDuration(videoDurationMs: number): number {
  if (!videoDurationMs) {
    return 0;
  }

  const seconds = Math.max(0, Math.floor(videoDurationMs / 1000));
  return Math.max(0, Math.floor(seconds * WORDS_PER_SECOND));
}

interface ScriptsTabProps {
  testID?: string;
  onDraftChanged?: () => void;
  onOpenCustomizeScript?: () => void;
  onWordCountOutOfRange?: (isOutOfRange: boolean) => void;
  onVersionSelected?: (versionIndex: number) => void;
  onTabChange: (tab: AiAvatarEditorTab, source?: string) => void;
}

const RootContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '0 24px 24px 24px',
  height: '100%',
  boxSizing: 'border-box',
  overflowY: 'auto',
  ...scrollbarMixins.overrideScrollbarHover(),
}));

function ScriptsTab({
  testID = 'scripts-tab',
  onDraftChanged,
  onOpenCustomizeScript,
  onWordCountOutOfRange,
  onVersionSelected,
  onTabChange,
}: ScriptsTabProps) {
  const [manualText, setManualText] = useState('');
  const {scriptData, scriptsSnapshot, uploadedVideos, assetId} =
    contentLabStore(
      useShallow((state) => ({
        scriptData: state.draftAiAvatarProject.scriptData,
        scriptsSnapshot: state.draftAiAvatarProject.scriptsSnapshot,
        uploadedVideos: state.uploadedVideos,
        assetId: state.draftAiAvatarProject.assetId,
      }))
    );

  const selectedScriptText = useMemo(() => {
    const idx = scriptData.selectedScriptIndex;
    if (idx === null || idx === undefined) return '';
    const scripts = scriptData.scripts || [];
    if (idx < 0 || idx >= scripts.length) return '';
    return scripts[idx]?.script || '';
  }, [scriptData]);

  const selectedVersionIdx = scriptData.selectedScriptIndex ?? 0;

  const wordCount = useMemo(() => {
    const trimmed = manualText.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).filter(Boolean).length;
  }, [manualText]);

  const isEdited = useMemo(() => {
    const currentScript = scriptData.scripts?.[selectedVersionIdx];
    const snapshotScript = scriptsSnapshot?.[selectedVersionIdx];

    if (!currentScript || !snapshotScript) {
      return false;
    }

    // Strict comparison of script text
    return currentScript.script !== snapshotScript.script;
  }, [scriptData.scripts, scriptsSnapshot, selectedVersionIdx]);

  const maxWords = useMemo(() => {
    const video =
      uploadedVideos.find((v) => v.id === assetId) || uploadedVideos[0];
    return getMaxWordsFromDuration(video?.durationMillis || 0);
  }, [uploadedVideos, assetId]);

  const tolerance = Math.floor(maxWords * WORD_COUNT_TOLERANCE);
  const minWords = Math.max(0, maxWords - tolerance);
  const maxWordsWithTolerance = maxWords + tolerance;
  const isOverLimit = wordCount > maxWordsWithTolerance;
  const isUnderLimit = wordCount < minWords;
  const isOutOfRange = isOverLimit || isUnderLimit;

  const totalScripts = (scriptData.scripts || []).length;

  const currentScriptTitle = useMemo(() => {
    const scripts = scriptData.scripts || [];
    const script = scripts[selectedVersionIdx];
    if (!script) return undefined;

    return script.title;
  }, [scriptData.scripts, selectedVersionIdx]);

  const handleAutoSave = useCallback((text: string) => {
    const current = contentLabStore.getState().draftAiAvatarProject;
    const existing = (current.scriptData?.scripts || []) as GeneratedScript[];
    let idx = current.scriptData?.selectedScriptIndex ?? 0;
    // Ensure there is at least one script slot
    const scripts: GeneratedScript[] = existing.length
      ? [...existing]
      : [
          {
            title: '',
            script: '',
            inputScriptConfiguration: {
              scriptStyle: ScriptStyle.Neutral,
            },
          },
        ];
    if (idx < 0 || idx >= scripts.length) idx = 0;
    const prevConfig =
      scripts[idx]?.inputScriptConfiguration ||
      ({
        scriptStyle: ScriptStyle.Neutral,
      } as GeneratedScript['inputScriptConfiguration']);
    const prevTitle = scripts[idx]?.title || '';
    scripts[idx] = {
      title: prevTitle,
      script: text,
      inputScriptConfiguration: prevConfig,
    };
    contentLabActions.updateDraftAiAvatarProjectScriptData({
      scripts,
      selectedScriptIndex: idx,
    });
  }, []);

  const handleVersionSelect = (idx: number) => {
    contentLabActions.updateDraftAiAvatarProjectScriptData({
      selectedScriptIndex: idx,
    });
    onDraftChanged?.();

    onVersionSelected?.(idx);
  };

  const handleTextChange = (text: string) => {
    setManualText(text);
    handleAutoSave(text);
  };

  useEffect(() => {
    setManualText(selectedScriptText);
  }, [selectedScriptText]);

  useEffect(() => {
    onWordCountOutOfRange?.(isOutOfRange);
  }, [isOutOfRange, onWordCountOutOfRange]);

  return (
    <RootContainer data-testid={testID}>
      <SuggestedScriptHeader
        currentIndex={selectedVersionIdx}
        totalScripts={totalScripts}
        onNavigate={(page) => {
          handleVersionSelect(page);
        }}
        onCustomize={() => onOpenCustomizeScript?.()}
      />

      <ScriptEditor
        title={currentScriptTitle}
        scriptNumber={selectedVersionIdx + 1}
        value={manualText}
        onChange={handleTextChange}
        onBlur={() => onDraftChanged?.()}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <WordCount
          wordCount={wordCount}
          minWords={minWords}
          maxWordsWithTolerance={maxWordsWithTolerance}
          isEdited={isEdited}
        />
        <Button
          appearance="neutral"
          mode="tinted"
          size="small"
          endIcon={
            <Icon
              name="Actions-Arrow-Forward-Small"
              size="medium"
              appearance="neutral"
            />
          }
          onClick={() =>
            onTabChange(AiAvatarEditorTab.AVATARS, 'select_an_avatar_click')
          }
        >
          <Label size="md" color={designSystemToken('semantic.fg.neutral')}>
            {translate('Select an avatar')}
          </Label>
        </Button>
      </Stack>
    </RootContainer>
  );
}

export default ScriptsTab;
